import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import { green, red } from "@mui/material/colors";

import { withTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { debounce, isEmpty } from "lodash";
import { addDays, format } from "date-fns";

import fpResourceUtilizationService from "@plannel/services/fp/fp-resource-utilization-service";
import VersionSearchGroup from "@plannel/pages/factory-plan/common/component/VersionSearchGroup";
import PeriodSearchGroup, { BUCKET_CASE } from "@plannel/pages/factory-plan/common/component/PeriodSearchGroup";
import SearchOptsGroupRetrieve from "@plannel/pages/factory-plan/common/component/SearchOptsGroupRetrieve";
import FpGridSetting, {
  readHide, buildGroupColumnPair, REPORT_TREE_GRID_OPTIONS, buildReportStatusBar,
  applyColumnOrder, buildDayBucketPivotColumnDefs, consumePendingFocus
} from "@plannel/pages/factory-plan/common/grid";
import { buildXAxisSortMap } from "@plannel/pages/factory-plan/common/chart";
import { getFilterStore } from "@plannel/components/aggrid/FilterStatusPanel";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import SplitterContainer from "@plannel/components/SplitterContainer";
import PivotChartComposed from "@plannel/components/rechart/PivotChartComposed";
import Snackbar from "@plannel/components/Snackbar";
import { DownloadButton } from "@plannel/components/ActionIconButton";
import ColorUtil from "@plannel/pages/utils/color-util";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";

const { snackBarMessageColor } = ColorUtil;

// old FP_SRH_RANGE.attr01Val(DB 단일 고정값) 이식 — planresult/PlanResult 파일럿에서 실제 확인된
// 값(9)을 그대로 재사용한다. 이 설정은 화면/뷰별로 분기되는 게 아니라 테넌트에 저장된 단일 값이라
// (fp-report-screen-guide.md §A-3c), old 소스의 로컬 fallback(14, DB 로드 전 임시값)이 아니라 그
// 실제 DB 값을 이식한다.
// TODO(MIG-T2): [resourceutilization/ResourceUtilization] 조회조건 세팅 3종(버킷 목록/검색필드
//   표시/검색구간)의 테넌트별 커스터마이징 미이관.
//   old: search-option/bucket · search-option/srh-options · setting FP_SRH_RANGE를 각각 DB에서 조회
//   사유: 정적 구성(JSON 카탈로그/고정 카탈로그/상수)으로 전환 — 가이드 §A-3~§A-3d에서 결정
const SEARCH_ZONE_DAYS = 9;

// old SearchOptsGroup이 이 화면에서 실제로 쓰던 필드 2개(loadData/loadChartData의 getValues 기준).
const SEARCH_FIELD_LABELS = { stageCdNm: "FP_STAGE", resourceCdNm: "FP_RESOURCE" };
const SEARCH_FIELD_KEYS = Object.keys(SEARCH_FIELD_LABELS);

const PARAM_DATE_FORMAT = "yyyyMMdd";

// old 백엔드 analysisIndicator 4종 고정 색상(ResourceUtilizationChart.jsx의 INDICATOR_CONFIG를
// 그대로 이식). itemCd처럼 임의 개수/값의 런타임 groupKey가 아니라 백엔드가 늘 이 4개만 내려주므로
// (ResourceUtilizationService#getResrcUtilization), PlanResult처럼 클릭 시점에 동적으로 시리즈를
// 조립할 필요 없이 화면 고정 배열로 선언한다(master-plan/reports/ResourceUtilization.js의 정적
// chartSeries 선례와 동일한 방식).
const INDICATOR_COLORS = {
  FP_PROCESS_TIME: "#6DE36F",
  JC_TIME: "#FC5F67",
  FP_DOWN_TIME: "#222E3C",
  FP_LEFT_TIME: "#FFC950",
};
const INDICATOR_ORDER = ["FP_PROCESS_TIME", "JC_TIME", "FP_DOWN_TIME", "FP_LEFT_TIME"];

// 트리(행 그룹) 계층 — 이 순서가 곧 그룹 depth다: 공장코드 > 공장명 > 스테이지코드 > 스테이지명 >
// 자원코드 > 자원명 > (leaf)분석지표. old 컬럼정의(plantGroup/stageGroup/resourceGroup 모두
// `mergeRule`)를 근거로 Row Grouping을 선택했다(Tree Data가 아님, fp-report-screen-guide.md §A-6b —
// old가 LocalTreeDataProvider가 아니라 mergeRule을 쓰고 백엔드 응답도 평평한 목록이라 판단 기준과 일치).
const GROUP_FIELDS = ["plantCd", "plantNm", "stageCd", "stageNm", "resourceCd", "resourceNm"];

const PLANT_CD_COL_ID = "plantCdGrp";
const PLANT_NM_COL_ID = `PLANT_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const STAGE_CD_COL_ID = "stageCdGrp";
const STAGE_NM_COL_ID = `STAGE_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const RESOURCE_CD_COL_ID = "resourceCdGrp";
const RESOURCE_NM_COL_ID = `RESOURCE_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;

const PLANT_NM_HIDE_KEY = "plantNmColsHide";
const STAGE_NM_HIDE_KEY = "stageNmColsHide";
const RESOURCE_NM_HIDE_KEY = "resourceNmColsHide";

const ResourceUtilization = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);

  // 딥링크 수신 1/2 — 탭이 아직 안 열려 있던 경우(fp-master-screen-guide.md §13-2 참고).
  // 발신 화면(dashboard/SimulationKPIDashboard)이 viewState에 조회조건을 심고 historyState로
  // 신호를 보내면 App.js가 탭을 열고 신호를 즉시 지우므로, 최초 마운트는 historyState가 아니라
  // 이 초기값 읽기로 프리필을 받는다(Item.js 등 Master 수신 화면과 동일 구조). 프리필이 없으면
  // (viewState에 조회조건 키 자체가 없음 — 컬럼 숨김 플래그만 있는 평상시) 기존 기본값 그대로다.
  const [versionState, setVersionState] = useState(() => ({
    planScope: reduxViewState?.planScope ?? "",
    planningDate: reduxViewState?.planningDate ? new ZDate(reduxViewState.planningDate) : null,
    versionCd: reduxViewState?.versionCd ?? "",
    plantCd: Array.isArray(reduxViewState?.plantCd) ? reduxViewState.plantCd : [],
    selectedVersion: null
  }));
  const [periodState, setPeriodState] = useState({
    startDate: null, endDate: null, bucketType: "", isEntirePlanPeriod: true
  });
  // stageCdNm/resourceCdNm — Report 공통 정책(§A-9b)대로 조회 버튼이 없다. 자유 텍스트는
  // SearchOptsGroupRetrieve(TextFieldSearch 기반) 안의 Enter/검색아이콘으로만 커밋한다.
  const stageCdNmRef = useRef("");
  const resourceCdNmRef = useRef("");

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);

  const [chartData, setChartData] = useState([]);
  const [chartLabels, setChartLabels] = useState([]);

  const latestGridReqRef = useRef(0);
  const latestChartReqRef = useRef(0);
  const prevChartParamsRef = useRef(null);
  const pendingFocusRef = useRef(null);
  const searchStateRef = useRef({ versionState, periodState });
  searchStateRef.current = { versionState, periodState };

  const debouncedRetrieveRef = useRef();
  if (!debouncedRetrieveRef.current) {
    debouncedRetrieveRef.current = debounce(() => retrieveResourceUtilization(), 200);
  }
  useEffect(() => () => debouncedRetrieveRef.current.cancel(), []);

  useEffect(() => () => getFilterStore(viewName).clear(), [viewName]);

  const defaultGridMemo = useMemo(() => {
    const base = FpGridSetting({ title, viewName });
    return {
      ...base,
      defaultColDef: { ...base.defaultColDef, editable: false },
      // toolResourceYn 셀 병합(아래 spanRows) 활성화 — CellSpanModule은 AllEnterpriseModule
      // 번들에 포함돼 있어 별도 모듈 등록은 필요 없다.
      enableCellSpan: true,
      ...REPORT_TREE_GRID_OPTIONS,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 핵심 지표(작업 교체 시간) 강조 — 분석 지표 컬럼에 건다. 배경색만(green[50]), 굵게는 안 씀 —
  // 아래 jcTimeCellStyle과 동일 기준으로 분석 지표 컬럼~피벗 컬럼 범위에만 적용된다
  // (MaterialPsi/PsiDetail/ProdSalesStockStatus와 동일 관례).
  const analysisIndicatorHighlightStyle = (params) => (
    params.data?.analysisIndicator === "JC_TIME" ? { backgroundColor: green[50] } : null
  );

  // 고정(왼쪽 pinned) 컬럼 — old fixColumns(plantCd/plantNm/stageCd/stageNm/resourceCd/resourceNm/
  // toolResourceYn/analysisIndicator)와 1:1. 공장/스테이지/자원은 행 그룹(트리)으로, toolResourceYn은
  // (그룹 내에서도 mergeRule이 걸려 있었지만 값이 자원 단위로 항상 동일해 굳이 그룹으로 만들 필요가
  // 없다 — 자원이 바뀌지 않는 한 같은 값이 반복 렌더되어 시각적으로는 old의 세로 병합과 동일한
  // 결과가 나온다) 평범한 field 컬럼, analysisIndicator는 트리의 leaf(실제 데이터 행을 구분하는 값)라
  // field 컬럼이다.
  const fixedColumnDefs = useMemo(() => [
    ...GROUP_FIELDS.map(field => ({
      headerName: field, field, colId: field, rowGroup: true, hide: true, suppressColumnsToolPanel: true
    })),

    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, PLANT_NM_HIDE_KEY),
      cdColId: PLANT_CD_COL_ID, cdField: "plantCd", cdHeader: "FP_PLANT_CD",
      nmColId: PLANT_NM_COL_ID, nmField: "plantNm", nmHeader: "PLANT_NM",
      hideKey: PLANT_NM_HIDE_KEY, width: 150
    }),
    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, STAGE_NM_HIDE_KEY),
      cdColId: STAGE_CD_COL_ID, cdField: "stageCd", cdHeader: "FP_STAGE_CD",
      nmColId: STAGE_NM_COL_ID, nmField: "stageNm", nmHeader: "FP_STAGE_NM",
      hideKey: STAGE_NM_HIDE_KEY, width: 150
    }),
    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, RESOURCE_NM_HIDE_KEY),
      cdColId: RESOURCE_CD_COL_ID, cdField: "resourceCd", cdHeader: "FP_RESOURCE_CD",
      nmColId: RESOURCE_NM_COL_ID, nmField: "resourceNm", nmHeader: "FP_RESOURCE_NM",
      hideKey: RESOURCE_NM_HIDE_KEY, width: 150
    }),

    // toolResourceYn — old dataType:"boolean" + mergeRule(자원 단위 세로 병합)를 AG Grid
    // colDef.spanRows로 재현(§0-4 booleanColumn 컨벤션, 읽기 전용이라 defaultColDef.editable:false로
    // 클릭해도 값이 안 바뀐다). 자원(resourceCd)이 바뀌는 경계와 그룹(접힌 상위) 행은 병합하지
    // 않는다 — resourceCd까지 같고 값도 같은 leaf 행끼리만 병합(2026-08-11 예외 승인, Tier2
    // 유보 원칙과 별개로 이 화면만 적용).
    {
      headerName: "FP_TOOL_RESOURCE_YN", field: "toolResourceYn", colId: "toolResourceYn", width: 130,
      pinned: "left", lockPinned: true, type: ["booleanColumn", "centerAligned"],
      // nodeA/nodeB는 nullable(SpanRowsParams) — 널 가드 없이 접근하면 조회 시 TypeError.
      spanRows: ({ nodeA, nodeB }) => (
        !!nodeA?.data && !!nodeB?.data && !nodeA.group && !nodeB.group &&
        nodeA.data.resourceCd === nodeB.data.resourceCd &&
        nodeA.data.toolResourceYn === nodeB.data.toolResourceYn
      )
    },

    // leaf(분석 지표) — old displayCallback가 SUM 여부와 무관하게 항상 transLangKey(value)로
    // 번역해서 보여준다(PlanResult의 itemCd와 달리 SUM일 때만이 아니라 매번 번역).
    {
      headerName: "FP_ANALYSIS_INDICATOR", field: "analysisIndicator", colId: "analysisIndicator", width: 150,
      pinned: "left", lockPinned: true, type: ["nonEditableColumn"],
      valueFormatter: (params) => t(params.value),
      cellStyle: analysisIndicatorHighlightStyle
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);

  // old columnProperty(setCrossTabGridLayoutAndOptions)의 피벗 셀 강조 대응 — JC_TIME 행은
  // green[50] 배경(굵게 없음, 위 analysisIndicatorHighlightStyle과 동일 기준). 값이 0보다 크면
  // 별개로 'shortage-cell'(빨간 글자) 경고가 겹친다(이건 지표 강조와 무관한 값 기반 경고라 유지).
  const jcTimeCellStyle = (params) => {
    if (params.data?.analysisIndicator !== "JC_TIME") return null;

    return {
      backgroundColor: green[50],
      color: params.value > 0 ? red[500] : undefined
    };
  };

  // old columnProperty.valueCallback — 0 이하 값은 빈 셀로 보여준다(그리드 전용, 차트에는 적용하지 않음).
  const suppressNonPositive = (value) => (value === null || value === undefined || value <= 0 ? null : value);

  // 요일 코드 번역+토/일 강조색은 common/grid.js buildDayBucketPivotColumnDefs가 처리한다
  // (old setColumnColorOnSunSat 대응, §A-6d/§A-6f). jcTimeCellStyle은 이 화면 고유 강조라
  // childExtraProps로 얹는다.
  const buildPivotColumnDefs = (crosstabHeader) => buildDayBucketPivotColumnDefs(crosstabHeader, t, {
    childExtraProps: () => ({ cellStyle: jcTimeCellStyle })
  });

  // old setCrossTabGridDataWithBucket — pivotData[i]를 crosstabHeader[i] 필드로 펼친다.
  // valueCallback(0 이하 → null)도 여기서 함께 적용한다(그리드 전용).
  const buildRowData = (crosstabHeader, data) => (data ?? []).map(row => {
    const flat = {
      plantCd: row.plantCd, plantNm: row.plantNm,
      stageCd: row.stageCd, stageNm: row.stageNm,
      resourceCd: row.resourceCd, resourceNm: row.resourceNm,
      toolResourceYn: row.toolResourceYn,
      analysisIndicator: row.analysisIndicator
    };
    (crosstabHeader ?? []).forEach((header, index) => {
      flat[header] = suppressNonPositive(row.pivotData?.[index]);
    });
    return flat;
  });

  const toParamDate = (date) => (date ? format(date, PARAM_DATE_FORMAT) : null);

  const buildBaseParams = () => {
    const { versionState: version, periodState: period } = searchStateRef.current;
    return {
      viewId: viewName,
      subId: "",
      planScope: version.planScope,
      versionCd: version.versionCd,
      stageCdNm: stageCdNmRef.current,
      resourceCdNm: resourceCdNmRef.current,
      bucketType: period.bucketType,
      fromDt: toParamDate(period.startDate),
      toDt: toParamDate(period.endDate),
      usePlanPeriod: !!period.isEntirePlanPeriod
    };
  };

  const clearChart = () => {
    prevChartParamsRef.current = null;
    setChartData([]);
    setChartLabels([]);
    getFilterStore(viewName).clear();
  };

  // 하단 상태바 브레드크럼 — PlanResult.js의 originLoc/destLoc/item 슬롯 매핑과 동일 원리
  // (공장→originLoc, 스테이지→destLoc은 슬롯이 부족해 item 슬롯에 자원을 싣는다 — FP_ANALYSIS의
  // "공장 > 스테이지 > 자원" 3단 계층을 FilterStatusPanel의 4개 순서 슬롯 중 1·2·4번에 맞춘다).
  const updateStatusBreadcrumb = (target) => {
    getFilterStore(viewName).setValue({
      originLocCd: target.plantCd ?? null, originLocCdHeader: t("FP_PLANT_CD"),
      originLocNm: target.plantNm ?? null, originLocNmHeader: t("PLANT_NM"),
      destLocCd: target.stageCd ?? null, destLocCdHeader: t("FP_STAGE_CD"),
      destLocNm: target.stageNm ?? null, destLocNmHeader: t("FP_STAGE_NM"),
      itemCd: target.resourceCd ?? null, itemCdHeader: t("FP_RESOURCE_CD"),
      itemNm: target.resourceNm ?? null, itemNmHeader: t("FP_RESOURCE_NM")
    });
  };

  // 클릭 기준으로 차트를 다시 조회한다. old updateChartFromGrid와 동일 — 상위 레벨을 클릭하면
  // 하위 코드는 비워서 보낸다(백엔드가 useGrouping:true일 때 이 레벨로 집계).
  const loadChart = (target) => {
    const { versionState: version } = searchStateRef.current;
    if (!version.planScope || !version.versionCd) return;

    updateStatusBreadcrumb(target);

    const nextParams = {
      plantCd: target.plantCd ?? "", stageCd: target.stageCd ?? "", resourceCd: target.resourceCd ?? ""
    };
    const prev = prevChartParamsRef.current;
    if (prev && prev.plantCd === nextParams.plantCd && prev.stageCd === nextParams.stageCd
      && prev.resourceCd === nextParams.resourceCd) {
      return;
    }
    prevChartParamsRef.current = nextParams;

    const reqId = ++latestChartReqRef.current;
    // old loadChartData는 useGrouping:true(그리드 조회의 false와 반대) — 드릴다운 레벨(plant/stage
    // 단위)로 백엔드가 집계해서 내려준다(ResrcUtilizationCrosstabDto.merge, GroupingLevel 참고).
    const params = { ...buildBaseParams(), ...nextParams, useGrouping: true };

    // TODO(MIG-T2): [resourceutilization/ResourceUtilization] 그리드 컬럼 필터(값 목록 체크박스) 미이관.
    //   old: plantCd/stageCd/resourceCd/toolResourceYn/analysisIndicator에 RealGrid autoFilter,
    //        필터 선택값을 차트 조회의 changes(FilterParameter)로 함께 전송
    //   사유: 행 그룹(트리)으로 전환하면서 코드 컬럼이 hide:true인 데이터 컬럼이 돼 헤더 필터
    //        아이콘을 띄울 자리가 없어졌다. 백엔드는 changes를 계속 받으므로 2차에 트리거 UI만
    //        얹으면 된다(가이드 §A-6e와 동일한 결정) — 지금은 빈 필터로 보낸다.
    fpResourceUtilizationService.getResrcUtilizationChart(params, {})
      .then(response => {
        if (reqId !== latestChartReqRef.current) return;

        const { chartHeader, data } = response.data ?? {};
        const rows = data ?? [];

        if (isEmpty(rows) || isEmpty(chartHeader)) {
          setChartData([]);
          setChartLabels([]);
          return;
        }

        setChartLabels(chartHeader);
        // old makeChartDataset — analysisIndicator별로 한 행. PlanResult의 itemCd(런타임 값,
        // 개수 가변)와 달리 이 화면은 백엔드가 항상 이 4개 지표만 내려주므로(useProcessTime 등
        // ResourceUtilizationService 참고) 화면 고정 순서/색상 그대로 매핑한다.
        setChartData(rows.map(r => {
          const point = { analysisIndicator: r.analysisIndicator };
          chartHeader.forEach((header, index) => { point[header] = r.pivotData?.[index] ?? 0; });
          return point;
        }));
      })
      .catch(e => {
        prevChartParamsRef.current = null;
        debug(e);
      });
  };

  const retrieveResourceUtilization = () => {
    const { versionState: version, periodState: period } = searchStateRef.current;

    if (!version.planScope || !version.versionCd) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_VERSION") });
      return;
    }
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_PLAN_PERIOD") });
      return;
    }

    const reqId = ++latestGridReqRef.current;
    // old loadData — useGrouping:false로 명시(공장/스테이지/자원/지표별 세부 행 전부 요청).
    const params = { ...buildBaseParams(), plantCds: version.plantCd, useGrouping: false };

    gridRef.current?.api?.setGridOption("loading", true);

    fpResourceUtilizationService.getResrcUtilization(params)
      .then(response => {
        if (reqId !== latestGridReqRef.current) return;

        const { crosstabHeader, data } = response.data ?? {};
        const api = gridRef.current?.api;
        if (!api) return;

        const nextColumnDefs = [...fixedColumnDefs, ...buildPivotColumnDefs(crosstabHeader)];
        GridUtils.setColumnDefs({ api, columnDefs: nextColumnDefs, viewName, initState: false });
        applyColumnOrder(api, nextColumnDefs);

        const rows = buildRowData(crosstabHeader, data);
        setRowData(rows);

        if (isEmpty(rows)) {
          clearChart();
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
          return;
        }

        // old: 조회 직후 첫 행의 공장 기준으로 차트를 그리고, 그리드의 선택 셀도 그 행으로 옮긴다.
        pendingFocusRef.current = { rowIndex: 0, colId: PLANT_CD_COL_ID };
        prevChartParamsRef.current = null;
        loadChart({ plantCd: rows[0].plantCd, plantNm: rows[0].plantNm });
      })
      .catch(e => {
        if (reqId === latestGridReqRef.current) clearChart();
        debug(e);
      })
      .finally(() => {
        if (reqId === latestGridReqRef.current) gridRef.current?.api?.setGridOption("loading", false);
      });
  };

  const onGridReady = (params) => {
    GridUtils.setColumnDefs({ ...params, columnDefs: fixedColumnDefs, viewName, initState: false });
  };

  const onRowDataUpdated = () => consumePendingFocus(gridRef, pendingFocusRef);

  const PLANT_LEVEL_COL_IDS = new Set([PLANT_CD_COL_ID, PLANT_NM_COL_ID]);
  const STAGE_LEVEL_COL_IDS = new Set([STAGE_CD_COL_ID, STAGE_NM_COL_ID]);

  // 클릭한 셀에서 차트를 그릴 드릴다운 대상을 뽑는다(old updateChartFromGrid의
  // `column.startsWith('plant'|'stage')` 분기를 트리 표시 컬럼 colId 기준으로 옮긴 것).
  // 그룹 행(접힌 그룹의 대표 행)은 조상 체인을 모으고, leaf(자원×지표) 행은 클릭한 컬럼으로
  // 레벨을 정한다 — 공장 칸=공장 레벨, 스테이지 칸=스테이지 레벨, 그 외(자원/toolResourceYn/
  // analysisIndicator/피벗 데이터 칸)는 old처럼 자원(최하위) 레벨 전체를 그대로 쓴다.
  const resolveChartTarget = (params) => {
    const node = params.node;
    if (!node) return null;

    if (node.group) {
      const target = {};
      for (let current = node; current; current = current.parent) {
        if (current.field) target[current.field] = current.key;
      }
      return isEmpty(target) ? null : target;
    }

    const row = node.data;
    if (!row) return null;

    const colId = params.column?.getColId() ?? "";
    if (PLANT_LEVEL_COL_IDS.has(colId)) {
      return { plantCd: row.plantCd, plantNm: row.plantNm };
    }
    if (STAGE_LEVEL_COL_IDS.has(colId)) {
      return { plantCd: row.plantCd, plantNm: row.plantNm, stageCd: row.stageCd, stageNm: row.stageNm };
    }
    return {
      plantCd: row.plantCd, plantNm: row.plantNm, stageCd: row.stageCd, stageNm: row.stageNm,
      resourceCd: row.resourceCd, resourceNm: row.resourceNm
    };
  };

  const onCellClicked = (params) => {
    const target = resolveChartTarget(params);
    if (target) loadChart(target);
  };

  const statusBar = useMemo(() => buildReportStatusBar(viewName), [viewName]);

  // 버전이 바뀌면 조회 구간을 다시 계산한다(old setVersionPeriod) — PlanResult.js와 동일 원리.
  useEffect(() => {
    const version = versionState.selectedVersion;
    if (!version?.startTs) {
      setPeriodState(prev => (prev.startDate || prev.endDate) ? { ...prev, startDate: null, endDate: null } : prev);
      return;
    }

    const startDate = new Date(version.startTs);
    const endDate = addDays(startDate, SEARCH_ZONE_DAYS);
    setPeriodState(prev => ({ ...prev, startDate, endDate }));

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.selectedVersion]);

  useEffect(() => {
    if (versionState.versionCd) return;

    debouncedRetrieveRef.current.cancel();

    const api = gridRef.current?.api;
    if (api) {
      GridUtils.setColumnDefs({ api, columnDefs: fixedColumnDefs, viewName, initState: false });
      applyColumnOrder(api, fixedColumnDefs);
    }
    setRowData([]);
    clearChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.versionCd]);

  // 선택형 조건(버전/plant/기간/버킷)이 바뀌면 값이 준비되는 즉시 자동 조회한다(§A-9b).
  useEffect(() => {
    const version = searchStateRef.current.versionState;
    const period = searchStateRef.current.periodState;
    if (!version.planScope || !version.versionCd) return;
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) return;

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    periodState.startDate, periodState.endDate, periodState.bucketType, periodState.isEntirePlanPeriod,
    versionState.plantCd
  ]);

  // 딥링크 수신 2/2 — 탭이 이미 열려 있던 경우(fp-master-screen-guide.md §13-2). historyState는
  // "다른 화면이 나에게 조회조건을 들고 이동해왔다"는 1회성 신호 채널이다(일반 탭 클릭 전환은
  // 이 채널을 타지 않음). 신호를 받으면 방금 심어진 viewState를 다시 읽어 versionState에 병합한다 —
  // planningDate를 항상 새 Date 인스턴스로 만들므로 같은 날짜여도 VersionSearchGroup의 버전 목록
  // 캐스케이드(effect deps가 참조 비교)가 재실행돼 versionCd 검증·selectedVersion 재계산·자동조회로
  // 이어진다. selectedVersion은 넘기지 않는다(캐스케이드가 재계산).
  const incomingNav = useSelector((state) => state.historyState.history);
  useEffect(() => {
    if (incomingNav !== viewName) return;
    const freshState = reduxUtil.getViewState(viewName);
    if (!freshState) return;

    setVersionState(prev => ({
      ...prev,
      planScope: freshState.planScope || prev.planScope,
      planningDate: freshState.planningDate ? new ZDate(freshState.planningDate) : prev.planningDate,
      versionCd: freshState.versionCd || prev.versionCd,
      plantCd: Array.isArray(freshState.plantCd) ? freshState.plantCd : prev.plantCd,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingNav]);

  // old ResourceUtilizationChart.jsx는 ChartLegendPlugin을 plugins 배열에 실제로 포함시켜
  // 범례를 보여줬다(PlanResult와 달리 "설정은 있지만 plugins에 없어 실제로는 없었던" 경우가
  // 아님 — §A-7 "ChartLegendPlugin 판단 시 주의" 참고, old 소스를 직접 확인해 구분했다).
  // → PlanResult처럼 recharts 기본 <Legend/>를 CSS로 숨기지 않고 그대로 노출한다. 클릭 시
  // series 숨김+취소선(old ChartLegendPlugin의 인터랙션)은 Tier2로 유보한다.
  // TODO(MIG-T2): [resourceutilization/ResourceUtilization] 범례 클릭 시 시리즈 숨김+취소선 미이관.
  //   old: ChartLegendPlugin(DOM 커스텀 범례, 클릭 시 해당 지표 dataset 토글+취소선 스타일)
  //   사유: recharts 기본 Legend/PivotChartComposed에 대응 훅이 없음(가이드 §A-7 Tier2 확정 항목)
  const pivotConfig = useMemo(() => ({
    groupKey: "analysisIndicator",
    chartSeries: INDICATOR_ORDER.map(indicator => ({
      field: indicator, text: indicator, type: "bar",
      groupName: `${t("TIME")}(${t("FP_MINUTES")})`,
      typeOptions: { stackId: "resourceUtilization", fill: INDICATOR_COLORS[indicator], name: t(indicator) }
    })),
    include: chartLabels,
    // x축(주+월 등 버킷이 섞이면) 기본 사전순 정렬을 chartLabels 원래 순서로 덮어쓴다
    // (common/chart.js, fp-report-screen-guide.md §A-7 — 크로스탭 차트 화면 공통).
    xAxisSortMap: buildXAxisSortMap(chartLabels)
  }), [chartLabels, t]);

  return (
    <>
      <FilterContainer>
        <VersionSearchGroup
          value={versionState}
          onChange={(partial) => setVersionState(prev => ({ ...prev, ...partial }))}
        />
        <PeriodSearchGroup
          value={periodState}
          onChange={(partial) => setPeriodState(prev => ({ ...prev, ...partial }))}
          // Report 화면 bucketCase 배정표(fp-report-screen-guide.md §A-3) — ResProdPlan/
          // ResrcUtilization(이 화면) 2개만 WITHOUT_HOUR, 나머지는 전부 WITH_HOUR.
          bucketCase={BUCKET_CASE.WITHOUT_HOUR}
        />
        <SearchOptsGroupRetrieve
          fieldRefs={{ stageCdNm: stageCdNmRef, resourceCdNm: resourceCdNmRef }}
          fieldKeys={SEARCH_FIELD_KEYS}
          labels={SEARCH_FIELD_LABELS}
          retrieve={retrieveResourceUtilization}
        />
        <Box sx={{ display: "flex" }}>
          <DownloadButton
            onClick={() => gridRef.current?.api?.exportDataAsExcel({ fileName: `${t(title)}.xlsx`, sheetName: t(title) })}
          />
        </Box>
      </FilterContainer>
      <SplitterContainer initialSizes={[1, 1]}>
        <Box id="fp-resource-utilization-grid" className="ag-theme-balham grid-area" sx={{ height: "calc(100% - 8px)", pb: 1 }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            {...defaultGridMemo}
            statusBar={statusBar}
            onGridReady={onGridReady}
            onRowDataUpdated={onRowDataUpdated}
            onCellClicked={onCellClicked}
          />
        </Box>
        <Box className="chart-area" sx={{ height: "calc(100% - 8px)", mt: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
            {/* TODO(MIG-T2): [resourceutilization/ResourceUtilization] 차트 장식성 인터랙션 2종 미이관.
                  old: ChartHatchPatternPlugin(빗금), ChartDataLabelPlugin(막대 위 수치)
                  사유: recharts/PivotChartComposed에 대응 훅이 없고, 1차 목표(조회·표시)에 불필요 — 가이드 §A-7에서 Tier2 확정 */}
            <PivotChartComposed
              pivotConfig={pivotConfig}
              data={chartData}
              options={{ tooltipContentStyle: { fontSize: "12px" }, minHeight: "0px", decimalPoint: true }}
              showConfigurationPopup={false}
              nullConvert
            />
          </Box>
        </Box>
      </SplitterContainer>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(ResourceUtilization);
